import { useState } from "react";
import axios from "axios";
import { Mail, Phone, User, MessageSquare, FileText } from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from "react-icons/fa";



export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name || !form.email || !form.phone || !form.message) {
            setError("Please fill all required fields");
            return;
        }

        setLoading(true);

        try {
            if (!executeRecaptcha) {
                setError("reCAPTCHA not loaded. Please refresh page.");
                setLoading(false);
                return;
            }

            const token = await executeRecaptcha("contact_form");

            const res = await axios.post(
                "http://127.0.0.1:8000/api/contact",
                {
                    ...form,
                    recaptcha_token: token,
                }
            );

            if (res.data.success) {
                setSuccess("Message sent successfully!");
                setForm({ name: "", email: "", phone: "", subject: "", message: "" });
            } else {
                setError(res.data.message || "Failed to send message");
            }

        } catch (err) {
            setError(
                err.response?.data?.message ||
                "reCAPTCHA verification failed or server error."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="bg-gradient-to-br from-orange-50 via-white to-orange-50 py-12 sm:py-16 px-4 sm:px-6">
            <div className="max-w-screen-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">

                {/* LEFT SIDE INFO */}

                <div className="flex flex-col justify-center text-center md:text-left">

                    <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] bg-clip-text text-transparent mb-4">
                        Let’s Talk
                    </h2>

                    <p className="text-gray-600 mb-6 text-sm sm:text-base">
                        Have questions or need help booking your tour? Fill out the form and our team will respond as quickly as possible.
                    </p>

                    <div className="space-y-4 text-gray-600 text-sm sm:text-base">

                        <div className="flex items-center gap-3 justify-center md:justify-start">
                            <FaMapMarkerAlt className="text-[#FF6B35]" />
                            <span>Dubai, UAE</span>
                        </div>

                        <div className="flex items-center gap-3 justify-center md:justify-start">
                            <FaPhoneAlt className="text-[#FF6B35]" />
                            <span>+971 123 456 789</span>
                        </div>

                        <div className="flex items-center gap-3 justify-center md:justify-start">
                            <FaEnvelope className="text-[#FF6B35]" />
                            <span>support@yourwebsite.com</span>
                        </div>

                    </div>

                </div>

                {/* FORM */}
                <div className="bg-white/90 backdrop-blur-lg shadow-xl rounded-2xl p-5 sm:p-8 border border-[#FF6B35]/20">

                    <h3 className="text-lg sm:text-xl font-semibold mb-6 text-gray-800 text-center">
                        Send Message
                    </h3>

                    {success && (
                        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
                            {success}
                        </div>
                    )}

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">

                        {/* Name */}
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                size={18}
                            />
                            <input
                                type="text"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                placeholder="Your Name"
                                className="w-full pl-10 border rounded-lg px-4 py-3 text-sm sm:text-base focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none"
                            />
                        </div>

                        {/* Email */}
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                size={18}
                            />
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                placeholder="Your Email"
                                className="w-full pl-10 border rounded-lg px-4 py-3 text-sm sm:text-base focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none"
                            />
                        </div>


                        {/* Phone */}
                        <div className="relative">
                            <Phone
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                size={18}
                            />

                            <input
                                type="tel"
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                placeholder="Your Phone"
                                className="w-full pl-10 pr-4 py-3 border rounded-lg text-sm sm:text-base focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none"
                            />
                        </div>

                        {/* Subject */}
                        <div className="relative">
                            <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                size={18}
                            />
                            <input
                                type="text"
                                name="subject"
                                value={form.subject}
                                onChange={handleChange}
                                placeholder="Subject"
                                className="w-full pl-10 border rounded-lg px-4 py-3 text-sm sm:text-base focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none"
                            />
                        </div>

                        {/* Message */}
                        <div className="relative">
                            <MessageSquare
                                className="absolute left-3 top-4 text-gray-400"
                                size={18}
                            />

                            <textarea
                                name="message"
                                rows="4"
                                value={form.message}
                                onChange={handleChange}
                                placeholder="Write your message..."
                                className="w-full pl-10 pr-4 py-3 border rounded-lg text-sm sm:text-base focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none resize-none"
                            ></textarea>
                        </div>

                        {/* Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white py-3 rounded-lg font-semibold hover:shadow-[0_10px_25px_rgba(255,107,53,0.25)] transition-all duration-300 flex justify-center items-center gap-2 text-sm sm:text-base"
                        >
                            {loading ? (
                                <>
                                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    Sending...
                                </>
                            ) : (
                                "Send Message"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}